import React from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { PageHero, Reveal, SectionTitle, Kicker } from '../components/common';
import { img, timeline, progressItems, restoration } from '../mock/mock';

const GardenDevelopment = () => {
  return (
    <div>
      <PageHero image={img.glasshouse} kicker="Garden Development" title="Watch a garden take root" sub="From master plan to grand opening — follow the milestones bringing the garden to life." />

      {/* Timeline */}
      <section id="timeline" className="py-24 bg-cream scroll-mt-24">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="The Journey" title="A roadmap to 2027" /></Reveal>
          <div className="mt-16 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-forest/20 md:-translate-x-1/2" />
            {timeline.map((t, i) => (
              <Reveal key={t.year} delay={i * 80}>
                <div className={`relative flex md:items-center mb-12 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="hidden md:block md:w-1/2" />
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 z-10">
                    <span className={`flex items-center justify-center w-9 h-9 rounded-full ${t.done ? 'bg-forest' : 'bg-gold'} shadow-lg`}>
                      {t.done ? <CheckCircle2 className="w-5 h-5 text-cream" /> : <Circle className="w-4 h-4 text-white" />}
                    </span>
                  </div>
                  <div className={`pl-16 md:pl-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-14 md:text-right' : 'md:pl-14'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-sand">
                      <span className="font-display text-3xl text-gold">{t.year}</span>
                      <h3 className="font-display text-xl text-forest mt-1">{t.title}</h3>
                      <p className="text-ink/65 text-sm mt-2 leading-relaxed">{t.text}</p>
                      {t.done && <span className="inline-block mt-3 text-xs font-600 text-forest-light">Completed</span>}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Progress */}
      <section id="progress" className="py-24 bg-forest text-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <SectionTitle light kicker="Construction Progress" title="Where things stand today" sub="Tracking the major workstreams as the garden moves from vision to reality." />
            <div className="mt-10 space-y-6">
              {progressItems.map((p, i) => (
                <Reveal key={p.label} delay={i * 90}>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-cream/85">{p.label}</span>
                      <span className="text-gold-light font-600">{p.pct}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-forest-dark/70 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-gold to-gold-light transition-all duration-1000" style={{ width: `${p.pct}%` }} />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>
          <Reveal delay={150} className="grid grid-cols-2 gap-4">
            <img src={img.glasshouseInterior} alt="Construction" className="rounded-2xl h-72 w-full object-cover shadow-xl" />
            <img src={img.palmHouse} alt="Conservatory" className="rounded-2xl h-72 w-full object-cover shadow-xl mt-10" />
          </Reveal>
        </div>
      </section>

      {/* Restorative & Reimagine Initiative */}
      <section id="restoration" className="py-24 bg-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="Capital Campaign" title="The Restorative & Reimagine Initiative" sub="The garden rises on the historic Camp Truitt property. These are the real relics and site features we are lovingly restoring and reimagining for the future." /></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {restoration.map((r, i) => (
              <Reveal key={r.name} delay={(i % 3) * 100}>
                <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:-translate-y-1.5 transition-transform duration-300 h-full flex flex-col">
                  <div className="relative h-56 overflow-hidden">
                    <img src={img[r.img]} alt={r.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-forest-dark/85 text-cream text-xs font-600">{r.current}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display text-xl text-forest">{r.name}</h3>
                    <p className="text-ink/65 text-sm mt-2 leading-relaxed flex-1">{r.plan}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <p className="text-center text-ink/45 text-xs mt-10 max-w-2xl mx-auto">Site features reflect the Conservancy's real Restorative & Reimagine campaign; photos shown are representative until final site photography is available.</p>
          </Reveal>
        </div>
      </section>

      {/* Gallery strip */}
      <section className="py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="On Site" title="Glimpses from the ground" /></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {[img.fern, img.lushPath, img.woodland, img.meadow, img.azaleaField, img.gardenPath, img.formalGarden, img.poolGarden].map((src, i) => (
              <Reveal key={i} delay={(i % 4) * 80}>
                <div className="rounded-xl overflow-hidden shadow-md group">
                  <img src={src} alt={`Site ${i + 1}`} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GardenDevelopment;
